import { memo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import type { FlowNode, TriggerData } from '../../types';
import { Icon, type IconName } from '../Icon';
import { NodeIssueBadge } from './NodeIssueBadge';

const SOURCE_ICON: Record<string, IconName> = {
  'call.missed':        'phoneOff',
  'call.completed':     'phone',
  'sms.received':       'sms',
  'job.status_changed': 'list',
  'invoice.aged':       'invoice',
  'storm.hail_cell':    'storm',
  'lead.created':       'house',
  'schedule.cron':      'calendar',
};

function TriggerNodeImpl({ data, selected, id }: NodeProps<FlowNode>) {
  const d = data as TriggerData;
  const icon = SOURCE_ICON[d.source] || 'lightning';
  return (
    <div className={`vnode trig ${selected ? 'selected' : ''}`} data-node-id={id}>
      <NodeIssueBadge issues={(d as any).__issues} />
      <div className="vnode-head">
        <span className="vnode-lbl">
          <Icon name="lightning" size={11} variant="blue" />
          Trigger · when this happens
        </span>
      </div>

      {d.source === 'storm.hail_cell' && <HailCellPreview label={d.label} description={d.description} />}
      {d.source === 'call.missed' && <CallPreview missed label={d.label} description={d.description} />}
      {d.source === 'call.completed' && <CallPreview label={d.label} description={d.description} />}
      {d.source === 'lead.created' && <LeadCardPreview label={d.label} description={d.description} />}
      {d.source === 'invoice.aged' && <InvoicePreview label={d.label} description={d.description} />}
      {d.source === 'sms.received' && <SmsBubblePreview incoming label={d.label} description={d.description} />}
      {d.source === 'job.status_changed' && <JobStatusPreview label={d.label} description={d.description} />}
      {d.source === 'schedule.cron' && <ScheduledPreview label={d.label} description={d.description} />}

      <Handle type="source" position={Position.Bottom} id="out" />
    </div>
  );
}

export const TriggerNode = memo(TriggerNodeImpl);

/* ------------------ visual sub-cards ------------------ */

function HailCellPreview({ label, description }: { label: string; description?: string }) {
  return (
    <div className="vcard vcard-hail">
      <div className="vcard-map" aria-hidden="true">
        <svg viewBox="0 0 240 110" preserveAspectRatio="none">
          <defs>
            <radialGradient id="hg1" cx="35%" cy="40%" r="60%">
              <stop offset="0%" stopColor="#ff5f56" stopOpacity="0.95" />
              <stop offset="50%" stopColor="#ffbd2e" stopOpacity="0.7" />
              <stop offset="100%" stopColor="#5cd28a" stopOpacity="0.3" />
            </radialGradient>
            <pattern id="grid1" width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(195,163,91,0.12)" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="240" height="110" fill="rgba(11,9,9,0.6)" />
          <rect width="240" height="110" fill="url(#grid1)" />
          <polygon points="60,28 110,18 160,30 175,55 155,80 110,86 70,72 50,52" fill="url(#hg1)" opacity="0.85" />
          <circle cx="120" cy="50" r="3" fill="#ff5f56" />
          <text x="128" y="54" fill="#fff" fontSize="11" fontWeight="700" fontFamily="Oswald">2.18"</text>
          <text x="12" y="22" fill="#ffd07a" fontSize="9" fontWeight="800" letterSpacing="1.2">STORM CMD · LIVE</text>
        </svg>
      </div>
      <div className="vcard-body">
        <div className="vcard-row">
          <span className="vcard-ic"><Icon name="storm" size={15} variant="silver" /></span>
          <b>{label}</b>
        </div>
        <span className="vcard-meta">{description || 'NWS hail polygon · zip overlap'}</span>
      </div>
    </div>
  );
}

function CallPreview({ missed, label, description }: { missed?: boolean; label: string; description?: string }) {
  return (
    <div className="vcard vcard-call">
      <div className="vcard-call-head">
        <div className={`vcard-call-icon ${missed ? 'missed' : ''}`}>
          <Icon name={missed ? 'phoneOff' : 'phone'} size={18} variant="silver" />
        </div>
        <div className="vcard-call-info">
          <b>{missed ? 'Missed call' : 'Inbound call'}</b>
          <span>(469) 316-0530</span>
        </div>
      </div>
      <div className="vcard-call-meta">
        <span>{label}</span>
        <span style={{ color: 'var(--quiet)' }}>{description || (missed ? '4 rings · 0:00 · auto-text-back queued' : 'Caller-ID matched · recording')}</span>
      </div>
    </div>
  );
}

function LeadCardPreview({ label, description }: { label: string; description?: string }) {
  return (
    <div className="vcard vcard-lead">
      <div className="vcard-property" aria-hidden="true">
        <svg viewBox="0 0 240 110" preserveAspectRatio="none">
          <defs>
            <linearGradient id="sky1" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#7ba8c4" />
              <stop offset="60%" stopColor="#c8a87a" />
              <stop offset="100%" stopColor="#8c7245" />
            </linearGradient>
            <linearGradient id="grass1" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#5a7340" />
              <stop offset="100%" stopColor="#3a4f2a" />
            </linearGradient>
          </defs>
          <rect width="240" height="60" fill="url(#sky1)" />
          <rect y="60" width="240" height="50" fill="url(#grass1)" />
          <polygon points="55,35 195,35 175,55 75,55" fill="#3a2e22" />
          <rect x="75" y="55" width="100" height="35" fill="#d4b67a" />
          <rect x="118" y="65" width="14" height="25" fill="#5a4630" />
          <rect x="84" y="64" width="20" height="14" fill="#2e3a52" />
          <rect x="146" y="64" width="20" height="14" fill="#2e3a52" />
        </svg>
        <span className="property-photo-badge">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
            <path d="M3 8a2 2 0 012-2h2l1.5-2h7L17 6h2a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
            <circle cx="12" cy="13" r="3.5" stroke="currentColor" strokeWidth="2" />
          </svg>
          9
        </span>
      </div>
      <div className="vcard-body">
        <div className="vcard-row">
          <span className="vcard-ic vcard-ic-clock"><Icon name="clock" size={15} variant="muted" /></span>
          <b>{label}</b>
        </div>
        <span className="vcard-meta">
          <Icon name="dot" size={10} variant="silver" /> {description || '3906 Commonwealth Dr · Garland, TX 75043'}
        </span>
        <div className="vcard-fields">
          <div className="vcard-field">
            <small>PM</small>
            <b>Claude Williams</b>
          </div>
          <div className="vcard-field">
            <small>Damage</small>
            <b>Hail &amp; Wind</b>
          </div>
        </div>
      </div>
    </div>
  );
}

function InvoicePreview({ label, description }: { label: string; description?: string }) {
  return (
    <div className="vcard vcard-invoice">
      <div className="vcard-invoice-head">
        <span><Icon name="invoice" size={11} variant="silver" /> INVOICE</span>
        <b>INV-2412</b>
      </div>
      <div className="vcard-invoice-amount">$1,200.00</div>
      <div className="vcard-invoice-meta">
        <span>{label}</span>
        <span style={{ color: 'var(--signal-amber)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <Icon name="dot" size={8} variant="amber" />
          {description || '7 days overdue'}
        </span>
      </div>
    </div>
  );
}

function SmsBubblePreview({ incoming, label, description }: { incoming?: boolean; label: string; description?: string }) {
  return (
    <div className="vcard vcard-sms">
      <div className={`vcard-sms-bubble ${incoming ? 'in' : 'out'}`}>
        "{description || 'Hey, looking for an estimate on a 2-story after the May 14 hail.'}"
      </div>
      <div className="vcard-sms-meta">
        <b>{label}</b>
      </div>
    </div>
  );
}

function JobStatusPreview({ label, description }: { label: string; description?: string }) {
  return (
    <div className="vcard vcard-job">
      <div className="vcard-status-row">
        <span className="status-pill old">Inspection</span>
        <Icon name="arrowRight" size={14} variant="gold" />
        <span className="status-pill new">Proposal sent</span>
      </div>
      <div className="vcard-body">
        <b>{label}</b>
        <span className="vcard-meta">{description || 'Tech updates status in the app'}</span>
      </div>
    </div>
  );
}

function ScheduledPreview({ label, description }: { label: string; description?: string }) {
  return (
    <div className="vcard vcard-scheduled">
      <div className="vcard-clock"><Icon name="calendar" size={18} variant="muted" /></div>
      <div className="vcard-body">
        <b>{label}</b>
        <span className="vcard-meta">{description || 'Runs every 90 days · batch job'}</span>
      </div>
    </div>
  );
}
